import { useContext, useEffect, useRef, useState } from "react";
import { getSvcSettingsClient, type SettingsGroup } from "sj-ui-toolkit/src/client/svcSettings";
import { getAspenGlobalEnabled, useToast } from "sj-ui-toolkit/src/hooks";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { AppContext } from "@/context/AppContext";
import {
  ALL_ASPEN_FEATURES_DISABLED,
  ASPEN_FEATURE_META,
  type AspenFeatureKey,
  type AspenFeaturesState,
} from "@/constants/aspen";

const aspenFeaturesCache = new Map<string, AspenFeaturesState>();
const aspenGlobalEnabledCache = new Map<string, boolean>();
const BACK_OFFICE_ASPEN_GROUP_NAME = "aspen.global";
const TENANT_ASPEN_GLOBAL_GROUP_NAME = "aspen.global";
const ASPEN_FEATURES_UPDATED_EVENT = "admin:aspen-features-updated";

interface AspenFeaturesUpdatedEventDetail {
  tenantName: string;
  features: AspenFeaturesState;
  enableAspenGlobally: boolean;
}

const buildFeatureState = (groups: SettingsGroup[]): AspenFeaturesState => {
  const assistGroup = groups.find(group => group.groupName === "aspen.assist");
  const guardianGroup = groups.find(group => group.groupName === "aspen.guardian");
  const adaptGroup = groups.find(group => group.groupName === "aspen.adapt");

  return {
    assist: (assistGroup?.values?.["hintsEnabled"] as boolean | undefined) ?? false,
    guardian: (guardianGroup?.values?.["enableAspenGuardian"] as boolean | undefined) ?? false,
    recordInstructionFiles:
      (guardianGroup?.values?.["recordInstructionFiles"] as boolean | undefined) ?? false,
    forwardCwes: (guardianGroup?.values?.["forwardCwes"] as boolean | undefined) ?? false,
    adapt: (adaptGroup?.values?.["enableAspenAdapt"] as boolean | undefined) ?? false,
    recordRepoName: (adaptGroup?.values?.["recordRepoName"] as boolean | undefined) ?? false,
    recordUsername: (adaptGroup?.values?.["recordUsername"] as boolean | undefined) ?? false,
    recordPrNumber: (adaptGroup?.values?.["recordPrNumber"] as boolean | undefined) ?? false,
  };
};

const resolveBackOfficeAspenEnabled = (groups: SettingsGroup[]): boolean => {
  const aspenGroup =
    groups.find(group => group.groupName === BACK_OFFICE_ASPEN_GROUP_NAME) ?? groups[0];
  const values = (aspenGroup?.values ?? {}) as Record<string, unknown>;
  return values["enableAspenGlobally"] !== false;
};

const emitAspenFeaturesUpdated = (
  tenantName: string,
  features: AspenFeaturesState,
  enableAspenGlobally: boolean
): void => {
  if (!tenantName || typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent<AspenFeaturesUpdatedEventDetail>(ASPEN_FEATURES_UPDATED_EVENT, {
      detail: { tenantName, features, enableAspenGlobally },
    })
  );
};

export interface UseAspenFeaturesOptions {
  enabled?: boolean;
}

export const useAspenFeatures = ({ enabled = true }: UseAspenFeaturesOptions = {}) => {
  const {
    state: { viewOnly },
  } = useContext(AppContext);
  const currentTenantName = getCurrentSelectedTenant() ?? "";
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isBackOfficeAspenEnabled, setIsBackOfficeAspenEnabled] = useState(true);
  const [enableAspenGlobally, setEnableAspenGlobally] = useState(true);
  const [features, setFeatures] = useState<AspenFeaturesState>({ ...ALL_ASPEN_FEATURES_DISABLED });
  const featuresRef = useRef(features);

  useEffect(() => {
    featuresRef.current = features;
  }, [features]);

  useEffect(() => {
    if (!currentTenantName || typeof window === "undefined") return;

    const handleAspenFeaturesUpdated = (event: Event): void => {
      const customEvent = event as CustomEvent<AspenFeaturesUpdatedEventDetail>;
      const detail = customEvent.detail;

      if (!detail || detail.tenantName !== currentTenantName) {
        return;
      }

      setFeatures(detail.features);
      featuresRef.current = detail.features;
      aspenFeaturesCache.set(currentTenantName, detail.features);
      setEnableAspenGlobally(detail.enableAspenGlobally);
      aspenGlobalEnabledCache.set(currentTenantName, detail.enableAspenGlobally);
    };

    window.addEventListener(ASPEN_FEATURES_UPDATED_EVENT, handleAspenFeaturesUpdated);

    return () => {
      window.removeEventListener(ASPEN_FEATURES_UPDATED_EVENT, handleAspenFeaturesUpdated);
    };
  }, [currentTenantName]);

  useEffect(() => {
    if (!enabled || !currentTenantName) {
      setFeatures({ ...ALL_ASPEN_FEATURES_DISABLED });
      featuresRef.current = { ...ALL_ASPEN_FEATURES_DISABLED };
      setIsBackOfficeAspenEnabled(true);
      setEnableAspenGlobally(true);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const load = async () => {
      try {
        const { promise: backOfficePromise } = getSvcSettingsClient().getBackOfficeSettings(
          currentTenantName,
          BACK_OFFICE_ASPEN_GROUP_NAME
        );
        const backOfficeData = await backOfficePromise;
        if (cancelled) return;

        const backOfficeEnabled = resolveBackOfficeAspenEnabled(backOfficeData?.groups ?? []);
        setIsBackOfficeAspenEnabled(backOfficeEnabled);

        if (!backOfficeEnabled) {
          const disabledFeatures = { ...ALL_ASPEN_FEATURES_DISABLED };
          setFeatures(disabledFeatures);
          featuresRef.current = disabledFeatures;
          aspenFeaturesCache.delete(currentTenantName);
          aspenGlobalEnabledCache.delete(currentTenantName);
          return;
        }

        const cachedFeatures = aspenFeaturesCache.get(currentTenantName);
        const cachedGlobalEnabled = aspenGlobalEnabledCache.get(currentTenantName);
        if (cachedFeatures && cachedGlobalEnabled !== undefined) {
          setFeatures({ ...cachedFeatures });
          featuresRef.current = { ...cachedFeatures };
          setEnableAspenGlobally(cachedGlobalEnabled);
          return;
        }

        const { promise } = getSvcSettingsClient().getTenantSettings(currentTenantName, "aspen");
        const data = await promise;
        if (cancelled) return;

        const groups = data?.groups ?? [];
        const nextFeatures = buildFeatureState(groups);
        const nextGlobalEnabled = getAspenGlobalEnabled(groups);
        setFeatures(nextFeatures);
        featuresRef.current = nextFeatures;
        aspenFeaturesCache.set(currentTenantName, nextFeatures);
        setEnableAspenGlobally(nextGlobalEnabled);
        aspenGlobalEnabledCache.set(currentTenantName, nextGlobalEnabled);
      } catch (error) {
        console.error("Error fetching Aspen feature settings:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [currentTenantName, enabled]);

  const buildGroupPayload = (group: string, override: Record<string, boolean> = {}) => {
    const current = featuresRef.current;
    const payloads: Record<string, Record<string, boolean>> = {
      "aspen.assist": { hintsEnabled: current.assist },
      "aspen.guardian": {
        enableAspenGuardian: current.guardian,
        recordInstructionFiles: current.recordInstructionFiles,
        forwardCwes: current.forwardCwes,
      },
      "aspen.adapt": {
        enableAspenAdapt: current.adapt,
        recordRepoName: current.recordRepoName,
        recordUsername: current.recordUsername,
        recordPrNumber: current.recordPrNumber,
      },
    };

    return { ...payloads[group], ...override };
  };

  const handleSaveError = (detail: string) => {
    showToast({
      severity: "error",
      summary: "Unable to save Aspen settings",
      detail,
    });
  };

  const toggleFeature = async (key: AspenFeatureKey, value: boolean) => {
    if (!currentTenantName || !isBackOfficeAspenEnabled || !enableAspenGlobally) return;

    const { group, field } = ASPEN_FEATURE_META[key];
    const previousFeatures = featuresRef.current;
    const enabledAdapt = key === "forwardCwes" && value;
    const nextFeatures = {
      ...previousFeatures,
      [key]: value,
      ...(enabledAdapt ? { adapt: true } : {}),
    };

    setFeatures(nextFeatures);
    featuresRef.current = nextFeatures;

    try {
      const { promise } = getSvcSettingsClient().updateTenantSettings(
        currentTenantName,
        group,
        buildGroupPayload(group, { [field]: value })
      );
      await promise;

      if (enabledAdapt) {
        const { promise: adaptPromise } = getSvcSettingsClient().updateTenantSettings(
          currentTenantName,
          "aspen.adapt",
          buildGroupPayload("aspen.adapt", { enableAspenAdapt: true })
        );
        await adaptPromise;
      }

      aspenFeaturesCache.set(currentTenantName, featuresRef.current);
      aspenGlobalEnabledCache.set(currentTenantName, enableAspenGlobally);
      emitAspenFeaturesUpdated(currentTenantName, featuresRef.current, enableAspenGlobally);
    } catch (error) {
      console.error(`Error updating Aspen feature "${key}":`, error);
      setFeatures(previousFeatures);
      featuresRef.current = previousFeatures;
      handleSaveError(`"${key}" could not be updated. Please try again.`);
    }
  };

  const toggleGlobal = async (enabled: boolean) => {
    if (!currentTenantName || !isBackOfficeAspenEnabled) return;

    const nextEnabled = enabled;
    const previousEnabled = enableAspenGlobally;
    setEnableAspenGlobally(nextEnabled);

    try {
      const { promise } = getSvcSettingsClient().updateTenantSettings(
        currentTenantName,
        TENANT_ASPEN_GLOBAL_GROUP_NAME,
        { enableAspenGlobally: nextEnabled }
      );
      await promise;

      aspenGlobalEnabledCache.set(currentTenantName, nextEnabled);
      emitAspenFeaturesUpdated(currentTenantName, featuresRef.current, nextEnabled);
    } catch (error) {
      console.error("Error updating Aspen global enablement:", error);
      setEnableAspenGlobally(previousEnabled);
      handleSaveError("Aspen feature settings could not be updated. Please try again.");
    }
  };

  const displayFeatures = enableAspenGlobally ? features : ALL_ASPEN_FEATURES_DISABLED;

  return {
    features: displayFeatures,
    isLoading,
    isBackOfficeAspenEnabled,
    viewOnly,
    currentTenantName,
    enableAspenGlobally,
    toggleFeature,
    toggleGlobal,
  };
};
